import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../../../components/common/PageHeader";
import Spinner from "../../../components/common/Spinner";
import ErrorAlert from "../../../components/common/ErrorAlert";
import { fetchComplianceRegister } from "../api";
import type { ComplianceRegister } from "@akfdev-com/shared/src/models/ComplianceRegister";

const STATUS_STYLES: Record<string, string> = {
  compliant: "bg-green-100 text-green-800",
  "due-soon": "bg-amber-100 text-amber-800",
  overdue: "bg-red-100 text-red-800",
  "not-applicable": "bg-gray-100 text-gray-800",
};

const ComplianceDashboard: React.FC = () => {
  const [items, setItems] = useState<ComplianceRegister[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setItems(await fetchComplianceRegister());
    } catch {
      setError("Failed to load compliance register.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(() => {
    return items.reduce(
      (acc, i) => {
        acc.total += 1;
        acc[i.status] = (acc[i.status] || 0) + 1;
        return acc;
      },
      { total: 0 } as Record<string, number>,
    );
  }, [items]);

  const upcoming = useMemo(() => {
    const horizon = new Date();
    horizon.setDate(horizon.getDate() + 30);
    return items
      .filter((i) => i.status !== "not-applicable" && new Date(i.nextDue) <= horizon)
      .sort((a, b) => a.nextDue.localeCompare(b.nextDue))
      .slice(0, 10);
  }, [items]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Compliance Dashboard"
        description="Overview of statutory inspections, insurance policies and obligations."
      />

      {loading && <Spinner />}
      {error && <ErrorAlert message={error} onRetry={load} />}

      {!loading && !error && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Total" value={counts.total} />
            <StatCard label="Compliant" value={counts.compliant || 0} tone="green" />
            <StatCard label="Due Soon" value={counts["due-soon"] || 0} tone="amber" />
            <StatCard label="Overdue" value={counts.overdue || 0} tone="red" />
            <StatCard label="N/A" value={counts["not-applicable"] || 0} />
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Upcoming in next 30 days</h3>
              <Link
                to="register"
                className="text-sm font-medium text-primary-700 hover:text-primary-800"
              >
                View register
              </Link>
            </div>
            {upcoming.length === 0 ? (
              <p className="p-6 text-sm text-gray-500">Nothing due in the next 30 days.</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <Th>Name</Th>
                    <Th>Category</Th>
                    <Th>Next Due</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {upcoming.map((i) => (
                    <tr key={i.id}>
                      <Td>{i.name}</Td>
                      <Td className="capitalize">{i.category.replace(/-/g, " ")}</Td>
                      <Td>{i.nextDue}</Td>
                      <Td>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            STATUS_STYLES[i.status] || ""
                          }`}
                        >
                          {i.status}
                        </span>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: number; tone?: "green" | "amber" | "red" }> = ({
  label,
  value,
  tone,
}) => {
  const toneClass =
    tone === "green"
      ? "text-green-700"
      : tone === "amber"
      ? "text-amber-700"
      : tone === "red"
      ? "text-red-700"
      : "text-gray-900";
  return (
    <div className="bg-white shadow rounded-lg p-4">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <td className={`px-4 py-2 text-sm text-gray-900 ${className || ""}`}>{children}</td>
);

export default ComplianceDashboard;
